import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Modal,
  Pressable,
  Linking,
  Platform,
  Image,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useTheme } from '../contexts/ThemeContext';
import {
  formatScheduleAsPlainText,
  ScheduleEditor,
  parseScheduleText,
  serializeScheduleDoc,
} from '../components/ScheduleEditor';
import {
  formatSetlistAsPlainText,
  StructuredDayListEditor,
  parseSetlistText,
  serializeSetlistDoc,
} from '../components/SetlistEditor';
import { DayNotesEditor } from '../components/calendar/DayNotesEditor';
import { CalendarWriterEditor } from '../components/calendar/CalendarWriterEditor';
import {
  STRUCTURED_DAY_LIST_LABELS,
  STRUCTURED_DAY_LIST_TABS,
} from '../components/structuredDayListLabels';
import { buildTabEntryPublishFields } from '../utils/calendarTabDraftService';
import { formatLodgingDaySummary } from '../utils/tourDayLodgingUtils';
import { DaySheetPaper, buildDaySheetPreviewRows, downloadDaySheetPdf } from '../components/DaySheetPaper';
import {
  SetlistPaper,
  formatSetlistDateLabel,
  openSetlistPrintWindow,
} from '../components/SetlistPaper';
import { WriterHtmlView } from '../components/calendar/WriterHtmlView';
import { isWriterHtmlEmpty } from '../utils/writerFormat';
import { isNotesHtml } from '../utils/notesFormat';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  onSnapshot,
  addDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { subscribeShareByToken, getFirestoreDb, parseShareModulesOrderList } from '../utils/calendarShareService';
import { DayFileThumbnail } from '../components/calendar/DayFileThumbnail';
import { isImageFileUrl } from '../utils/documentImport';

const CALENDAR_TOP_TABS = [
  'TOUR PARTY',
  'RIDER',
  'STAGE',
  'SETLIST',
  'GUEST LIST',
  'SCHEDULE',
  'FILES',
  'CONTACTS',
  'MERCH',
  'NOTES',
  'DAY SHEET',
  'LODGING',
];
const DAY_SHARE_OPTIONS = ['VENUES', ...CALENDAR_TOP_TABS];
const DAY_INFO_TAB_KEYS = { RIDER: 'RIDER_DAILY' };

function riderSectionHasContent(dailyHtml, fullHtml, riderFullFilesCount) {
  return (
    !isWriterHtmlEmpty(dailyHtml) ||
    !isWriterHtmlEmpty(fullHtml) ||
    riderFullFilesCount > 0
  );
}

function normalizeDayFileType(raw) {
  const key = String(raw || '').trim();
  if (key === 'files' || key === 'stage' || key === 'riderFull') return key;
  return 'riderFull';
}

/** Download row + thumbnail preview (Calendar day details). */
function DayFileAttachment({ file, styles, theme }) {
  const url = String(file?.url || '').trim();
  if (!url) return null;
  const image = isImageFileUrl(url);
  return (
    <View style={styles.detailFileBlock}>
      <TouchableOpacity style={styles.fileLinkRow} onPress={() => Linking.openURL(url).catch(() => {})}>
        <Ionicons name={image ? 'image-outline' : 'document-outline'} size={14} color={theme.brand.accent} />
        <Text style={styles.fileLinkText} numberOfLines={1}>
          {file.name}
        </Text>
      </TouchableOpacity>
      <DayFileThumbnail
        url={url}
        name={file.name}
        theme={theme}
        variant="detail"
        onPress={() => Linking.openURL(url).catch(() => {})}
        accessibilityLabel={`Open ${file.name}`}
      />
    </View>
  );
}

function resolveAppBaseUrl() {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin);
  }
  return 'https://vrc.app';
}

function readQueryParam(name) {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return '';
  try {
    const val = new URLSearchParams(window.location.search).get(name);
    return String(val || '').trim();
  } catch {
    return '';
  }
}

/** Comma-separated module labels from share link; null = no filter (show all). */
function parseShareModulesAllowList(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  let decoded = s;
  try {
    decoded = decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    decoded = s;
  }
  const parts = decoded
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  return new Set(parts);
}

function shareModuleTokenForSection(sec) {
  if (!sec) return '';
  if (sec.title === 'DAY SHEET PREVIEW') return 'DAY SHEET';
  return String(sec.title || '').trim();
}

const LINE_ALIGN_PREFIX = /^\[a:(left|center|right)\]\s*/i;

function parseLineAlignPrefix(line) {
  const s = String(line || '');
  const m = s.match(LINE_ALIGN_PREFIX);
  if (!m) return { align: 'left', body: s };
  const align = String(m[1] || 'left').toLowerCase();
  const a = align === 'center' || align === 'right' ? align : 'left';
  return { align: a, body: s.replace(LINE_ALIGN_PREFIX, '') };
}

function sanitizeNotesColorHex(hex) {
  const h = String(hex || '').trim();
  if (!/^#[0-9a-fA-F]{3}$/.test(h) && !/^#[0-9a-fA-F]{6}$/.test(h) && !/^#[0-9a-fA-F]{8}$/.test(h)) return null;
  return h;
}

/** Renders bold **markers** inside a plain string (no color tags). */
function renderBoldOnly(text, styles, keyPrefix) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    const isBold = /^\*\*[^*]+\*\*$/.test(part);
    const content = isBold ? part.slice(2, -2) : part;
    return (
      <Text key={`${keyPrefix}-b-${idx}`} style={isBold ? styles.detailBodyBold : undefined}>
        {content}
      </Text>
    );
  });
}

/**
 * Inline: **bold** and [color:#rrggbb]...[/color] (non-nested; inner may contain **).
 */
function renderInlineFormatted(text, styles, keyPrefix) {
  const raw = String(text || '');
  if (!raw.includes('[color:')) {
    return renderBoldOnly(raw, styles, keyPrefix);
  }
  const nodes = [];
  const re = /\[color:(#[0-9a-fA-F]{3,8})\]([\s\S]*?)\[\/color\]/gi;
  let last = 0;
  let m;
  let seg = 0;
  while ((m = re.exec(raw)) !== null) {
    const before = raw.slice(last, m.index);
    if (before) {
      nodes.push(...renderBoldOnly(before, styles, `${keyPrefix}-pre-${seg}`));
    }
    const hex = sanitizeNotesColorHex(m[1]);
    const inner = m[2] || '';
    if (hex) {
      nodes.push(
        <Text key={`${keyPrefix}-col-${seg}`} style={[styles.detailBody, { color: hex }]}>
          {renderBoldOnly(inner, styles, `${keyPrefix}-cin-${seg}`)}
        </Text>
      );
    } else {
      nodes.push(...renderBoldOnly(m[0], styles, `${keyPrefix}-bad-${seg}`));
    }
    last = re.lastIndex;
    seg += 1;
  }
  const tail = raw.slice(last);
  if (tail) {
    nodes.push(...renderBoldOnly(tail, styles, `${keyPrefix}-tail`));
  }
  return nodes;
}

function lineTextBaseStyle(styles, align) {
  return [styles.detailBody, align !== 'left' ? { textAlign: align } : null];
}

function renderNotesBody(text, styles, theme) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (isNotesHtml(raw)) {
    return Platform.OS === 'web' ? (
      <WriterHtmlView html={raw} theme={theme} />
    ) : (
      <Text style={styles.detailBody}>{raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}</Text>
    );
  }
  return renderRichBody(raw, styles);
}

function renderRichBody(text, styles) {
  const lines = String(text || '').split('\n');
  return lines.map((raw, idx) => {
    const line = raw.trimEnd();
    if (!line.trim()) return <Text key={`line-${idx}`}>{'\n'}</Text>;
    const { align, body } = parseLineAlignPrefix(line);
    if (/^---+$/.test(body.trim())) return <View key={`line-${idx}`} style={styles.detailDivider} />;
    if (body.startsWith('## ')) {
      return (
        <Text key={`line-${idx}`} style={[styles.detailBodyHeading, align !== 'left' ? { textAlign: align } : null]}>
          {renderInlineFormatted(body.slice(3), styles, `h-${idx}`)}
          {'\n'}
        </Text>
      );
    }
    if (body.startsWith('- [ ] ')) {
      return (
        <Text key={`line-${idx}`} style={lineTextBaseStyle(styles, align)}>
          {'\u2610 '}
          {renderInlineFormatted(body.slice(6), styles, `cb-${idx}`)}
          {'\n'}
        </Text>
      );
    }
    if (body.startsWith('- ')) {
      return (
        <Text key={`line-${idx}`} style={lineTextBaseStyle(styles, align)}>
          {'\u2022 '}
          {renderInlineFormatted(body.slice(2), styles, `b-${idx}`)}
          {'\n'}
        </Text>
      );
    }
    return (
      <Text key={`line-${idx}`} style={lineTextBaseStyle(styles, align)}>
        {renderInlineFormatted(body, styles, `p-${idx}`)}
        {'\n'}
      </Text>
    );
  });
}

/** Short subtitle under module titles (matches ref design). */
const SECTION_DESCRIPTIONS = {
  venues: 'Stops and locations for this day',
  notes: 'Notes and reminders',
  contacts: 'Contacts for this day',
  rider: 'Rider notes and files',
  schedule: 'Day timeline',
  setlist: 'Print-ready setlist for this show day',
  guestList: 'Guests for this day',
  merch: 'Merch for this day',
  stage: 'Stage notes and files',
  files: 'Downloadable files for this day',
  tourParty: 'Tour party for this day',
  lodging: 'Lodging for this day',
  daySheet: 'Schedule and aftershow for this day',
  daySheetPreview: 'Schedule and aftershow for this day',
};

/**
 * Collapsible section module (ref design): uppercase title + description, optional
 * right-side action, a divider header, and a body that hides when collapsed.
 */
function ModuleSection({ styles, theme, id, title, description, expanded, onToggle, rightAction, children }) {
  const isExpanded = expanded !== false;
  return (
    <View style={styles.moduleBlock}>
      <TouchableOpacity
        style={[styles.moduleHead, !isExpanded && styles.moduleHeadCollapsed]}
        onPress={() => onToggle?.(id)}
        activeOpacity={0.75}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
        accessibilityLabel={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
      >
        <View style={styles.moduleHeadText}>
          <Text style={styles.moduleTitle}>{title}</Text>
          {description ? <Text style={styles.moduleDesc}>{description}</Text> : null}
        </View>
        <View style={styles.moduleHeadRight}>
          {rightAction ? <View style={styles.moduleActions}>{rightAction}</View> : null}
          <View style={styles.moduleCollapseBtn}>
            <Ionicons name={isExpanded ? 'chevron-up' : 'chevron-down'} size={18} color={theme.text.primary} />
          </View>
        </View>
      </TouchableOpacity>
      {isExpanded ? <View style={styles.moduleBody}>{children}</View> : null}
    </View>
  );
}

export function CalendarDayDetails({ route }) {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const auth = getAuth();
  const routeUid = String(route?.params?.uid || '').trim();
  const routeDateKey = String(route?.params?.dateKey || '').trim();
  const routeTourId = String(route?.params?.tourId || route?.params?.tour || '').trim();
  const authUid = String(auth.currentUser?.uid || '').trim();
  const queryUid = readQueryParam('uid');
  const queryDateKey = readQueryParam('date');
  const queryTourId = readQueryParam('tour');
  const queryModules = readQueryParam('modules');
  const routeModules = String(route?.params?.modules ?? '').trim();
  const shareToken = String(route?.params?.shareToken || readQueryParam('share') || '').trim();
  const [liveShareModulesRaw, setLiveShareModulesRaw] = useState('');
  const modulesRaw = liveShareModulesRaw || queryModules || routeModules;
  const shareModulesOrderList = useMemo(
    () => parseShareModulesOrderList(liveShareModulesRaw || queryModules || routeModules),
    [liveShareModulesRaw, queryModules, routeModules]
  );
  const shareModulesAllowList = useMemo(() => {
    if (shareModulesOrderList?.length) return new Set(shareModulesOrderList);
    return parseShareModulesAllowList(modulesRaw);
  }, [shareModulesOrderList, modulesRaw]);
  /** Shared day links pass `?uid=` (calendar owner). That must win over the signed-in user so another account can open the link. */
  const shareOwnerUid = String(routeUid || queryUid || '').trim();
  const uid = shareOwnerUid || authUid || null;
  const dateKey = routeDateKey || queryDateKey || '';
  const [inferredTourId, setInferredTourId] = useState(routeTourId || queryTourId || '');
  const [loadingBootstrap, setLoadingBootstrap] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingTour, setLoadingTour] = useState(false);
  const tourId = inferredTourId || routeTourId || queryTourId || null;
  const weekdayLabel = useMemo(() => {
    if (!dateKey) return '';
    const d = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }, [dateKey]);
  const [tourName, setTourName] = useState('');
  const [tourFolderData, setTourFolderData] = useState(null);

  // Editing mode: only the calendar owner (signed-in, not a share/modules view) may edit.
  const isOwner = Boolean(authUid && uid && authUid === uid && tourId && !shareToken && !modulesRaw);
  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [daySheetNotesDraft, setDaySheetNotesDraft] = useState('');
  const [savingTabs, setSavingTabs] = useState({});
  const [fileBusy, setFileBusy] = useState(false);
  const [filesNonce, setFilesNonce] = useState(0);
  const [sheetNonce, setSheetNonce] = useState(0);
  const saveTimersRef = React.useRef({});

  // Section collapse state (ref design: every section is a collapsible module, default expanded).
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = useCallback(
    (id) => setCollapsedSections((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );
  const isSectionExpanded = useCallback((id) => !collapsedSections[id], [collapsedSections]);
  const [events, setEvents] = useState([]);
  const [tabEntries, setTabEntries] = useState({});
  const [dayFiles, setDayFiles] = useState([]);
  const [daySheetNotes, setDaySheetNotes] = useState('');
  const [daySheetBannerUrl, setDaySheetBannerUrl] = useState('');
  const [daySheetBackgroundUrl, setDaySheetBackgroundUrl] = useState('');
  const [daySheetHasPublishFlag, setDaySheetHasPublishFlag] = useState(false);
  const [daySheetSchedulePublished, setDaySheetSchedulePublished] = useState(false);
  const [publishedScheduleText, setPublishedScheduleText] = useState('');
  const [stagePlotUrl, setStagePlotUrl] = useState('');
  const [stageInputListUrl, setStageInputListUrl] = useState('');
  const [shareSelections, setShareSelections] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [daySheetPdfBusy, setDaySheetPdfBusy] = useState(false);
  const [tourDayVenueName, setTourDayVenueName] = useState('');
  const detailsLoading = loadingBootstrap || loadingEvents || loadingTabs || loadingSheet || loadingFiles || loadingTour;
  const daySheetExportNodeId = useMemo(
    () => `day-sheet-export-${String(tourId || 'tour')}-${String(dateKey || 'date')}`,
    [tourId, dateKey]
  );
  const daySheetHeadline = useMemo(() => {
    if (!dateKey) return '';
    const d = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(d.getTime())) return dateKey;
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [dateKey]);
  const setlistRaw = useMemo(() => String(tabEntries.SETLIST || '').trim(), [tabEntries]);
  const setlistDateLabel = useMemo(() => formatSetlistDateLabel(dateKey), [dateKey]);
  const isSetlistOnlyShare = useMemo(() => {
    if (!shareModulesAllowList || shareModulesAllowList.size !== 1) return false;
    return shareModulesAllowList.has('SETLIST');
  }, [shareModulesAllowList]);

  const handlePrintSetlist = useCallback(() => {
    if (Platform.OS !== 'web') return;
    try {
      openSetlistPrintWindow({
        dateLabel: setlistDateLabel,
        setlistRaw,
        tourName: tourName || 'Untitled tour',
      });
    } catch (err) {
      console.error('print setlist', err);
    }
  }, [setlistDateLabel, setlistRaw, tourName]);

  useEffect(() => {
    if (!shareToken) {
      setLiveShareModulesRaw('');
      return undefined;
    }
    const db = getFirestoreDb();
    return subscribeShareByToken(db, shareToken, (data) => {
      const mods = data?.link?.modules;
      if (Array.isArray(mods) && mods.length) {
        setLiveShareModulesRaw(mods.join(','));
      }
    });
  }, [shareToken]);

  useEffect(() => {
    if (routeTourId || queryTourId) {
      setInferredTourId(routeTourId || queryTourId);
      setLoadingBootstrap(false);
      return;
    }
    if (!uid || !dateKey) {
      setLoadingBootstrap(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingBootstrap(true);
        const db = getFirestore();
        const findTour = async (qy) => {
          const snap = await getDocs(qy);
          let found = '';
          snap.forEach((d) => {
            if (found) return;
            const row = d.data() || {};
            found = String(row.tourId || row.sourceTourId || '').trim();
          });
          return found;
        };
        const fromTabs = await findTour(
          query(collection(db, 'users', uid, 'calendarTabEntries'), where('date', '==', dateKey))
        );
        const fromFiles = fromTabs
          ? ''
          : await findTour(query(collection(db, 'users', uid, 'calendarDayFiles'), where('date', '==', dateKey)));
        const fromEvents =
          fromTabs || fromFiles
            ? ''
            : await findTour(query(collection(db, 'users', uid, 'calendarEvents'), where('date', '==', dateKey)));
        const resolved = fromTabs || fromFiles || fromEvents || '';
        if (!cancelled) setInferredTourId(resolved);
      } catch (err) {
        console.error('calendar day details infer tour', err);
      } finally {
        if (!cancelled) setLoadingBootstrap(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, dateKey, routeTourId, queryTourId]);

  useEffect(() => {
    if (!uid || !dateKey) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingEvents(true);
        const db = getFirestore();
        const eventFilters = [where('date', '==', dateKey)];
        if (tourId) eventFilters.push(where('sourceTourId', '==', tourId));
        const eventsSnap = await getDocs(query(collection(db, 'users', uid, 'calendarEvents'), ...eventFilters));
        if (cancelled) return;
        const dayEvents = [];
        eventsSnap.forEach((d) => dayEvents.push({ id: d.id, ...(d.data() || {}) }));
        setEvents(dayEvents);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })().catch((err) => console.error('calendar day details events', err));
    return () => {
      cancelled = true;
    };
  }, [uid, dateKey, tourId]);

  useEffect(() => {
    if (!uid || !tourId || !dateKey) {
      setTabEntries({});
      setLoadingTabs(false);
      return undefined;
    }
    setLoadingTabs(true);
    const db = getFirestore();
    const qy = query(
      collection(db, 'users', uid, 'calendarTabEntries'),
      where('tourId', '==', tourId),
      where('date', '==', dateKey)
    );
    const unsub = onSnapshot(
      qy,
      (tabSnap) => {
        const next = {};
        tabSnap.forEach((d) => {
          const row = d.data() || {};
          const tabKey = String(row.tabKey || '').trim();
          if (!tabKey) return;
          next[tabKey] = String(row.text || '');
        });
        setTabEntries(next);
        setLoadingTabs(false);
      },
      (err) => {
        console.error('calendar day details tabs', err);
        setLoadingTabs(false);
      }
    );
    return unsub;
  }, [uid, tourId, dateKey]);

  useEffect(() => {
    if (!uid || !tourId || !dateKey) {
      setDaySheetNotes('');
      setDaySheetBannerUrl('');
      setDaySheetBackgroundUrl('');
      setDaySheetHasPublishFlag(false);
      setDaySheetSchedulePublished(false);
      setPublishedScheduleText('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingSheet(true);
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'users', uid, 'calendarDaySheets', `${tourId}__${dateKey}`));
        if (cancelled) return;
        if (!snap.exists()) {
          setDaySheetNotes('');
          setDaySheetBannerUrl('');
          setDaySheetBackgroundUrl('');
          setDaySheetHasPublishFlag(false);
          setDaySheetSchedulePublished(false);
          setPublishedScheduleText('');
          return;
        }
        const row = snap.data() || {};
        setDaySheetNotes(String(row.aftershowText || ''));
        setDaySheetBannerUrl(String(row.bannerUrl || ''));
        setDaySheetBackgroundUrl(String(row.backgroundUrl || ''));
        const hasPublishFlag = Object.prototype.hasOwnProperty.call(row, 'schedulePublished');
        setDaySheetHasPublishFlag(hasPublishFlag);
        if (hasPublishFlag) {
          setDaySheetSchedulePublished(Boolean(row.schedulePublished));
          setPublishedScheduleText(row.publishedScheduleText != null ? String(row.publishedScheduleText) : '');
        } else {
          setDaySheetSchedulePublished(false);
          setPublishedScheduleText('');
        }
      } catch (err) {
        console.error('calendar day details day sheet', err);
      } finally {
        if (!cancelled) setLoadingSheet(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, tourId, dateKey, sheetNonce]);

  useEffect(() => {
    if (!uid || !tourId || !dateKey) {
      setDayFiles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingFiles(true);
        const db = getFirestore();
        const snap = await getDocs(
          query(
            collection(db, 'users', uid, 'calendarDayFiles'),
            where('tourId', '==', tourId),
            where('date', '==', dateKey)
          )
        );
        if (cancelled) return;
        const files = [];
        snap.forEach((d) => {
          const row = d.data() || {};
          const url = String(row.url || '').trim();
          if (!url) return;
          files.push({
            id: d.id,
            name: String(row.name || 'File'),
            url,
            fileType: normalizeDayFileType(row.fileType),
          });
        });
        setDayFiles(files);
      } catch (err) {
        console.error('calendar day details files', err);
      } finally {
        if (!cancelled) setLoadingFiles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, tourId, dateKey, filesNonce]);

  useEffect(() => {
    if (!uid || !tourId) {
      setTourName('');
      setTourDayVenueName('');
      setTourFolderData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingTour(true);
        const db = getFirestore();
        const userSnap = await getDoc(doc(db, 'users', uid));
        if (!userSnap.exists() || cancelled) return;
        const folders = userSnap.data()?.folders || {};
        const folder = folders?.[tourId];
        const name = folder?.tour?.name || folder?.name || '';
        const dayVenue = (folder?.tour?.days || [])
          .map((day) => ({
            date: String(day?.scheduledDate || '').trim(),
            venue: (day?.venues || []).map((v) => String(v?.name || '').trim()).find(Boolean) || '',
          }))
          .find((x) => x.date === dateKey)?.venue;
        if (!cancelled) {
          setTourName(String(name || ''));
          setTourDayVenueName(String(dayVenue || ''));
          setTourFolderData(folder?.tour || null);
        }
      } catch (err) {
        console.error('calendar day details tour name', err);
      } finally {
        if (!cancelled) setLoadingTour(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, tourId, dateKey]);

  const generatedShareLink = useMemo(() => {
    if (!uid || !tourId || !dateKey || shareSelections.length === 0) return '';
    const baseUrl = resolveAppBaseUrl();
    const modules = encodeURIComponent(shareSelections.join(','));
    return `${baseUrl}/calendar/day/${encodeURIComponent(dateKey)}?uid=${encodeURIComponent(uid)}&tour=${encodeURIComponent(
      tourId
    )}&modules=${modules}`;
  }, [uid, tourId, dateKey, shareSelections]);

  const scheduleTextForDaySheet = useMemo(() => {
    if (daySheetHasPublishFlag && daySheetSchedulePublished) {
      return String(publishedScheduleText || '');
    }
    if (daySheetHasPublishFlag && !daySheetSchedulePublished) {
      return '';
    }
    // No publish fields on calendarDaySheets: day sheet preview/PDF uses live SCHEDULE tab (share viewers + owner).
    return String(tabEntries.SCHEDULE || '');
  }, [daySheetHasPublishFlag, daySheetSchedulePublished, publishedScheduleText, tabEntries]);
  const schedulePreviewText = useMemo(
    () => formatScheduleAsPlainText(scheduleTextForDaySheet),
    [scheduleTextForDaySheet]
  );
  const daySheetRows = useMemo(
    () => buildDaySheetPreviewRows(scheduleTextForDaySheet, { useDefaults: false }),
    [scheduleTextForDaySheet]
  );
  const riderFullFiles = useMemo(
    () => dayFiles.filter((f) => normalizeDayFileType(f.fileType) === 'riderFull'),
    [dayFiles]
  );
  const stageFiles = useMemo(
    () => dayFiles.filter((f) => normalizeDayFileType(f.fileType) === 'stage'),
    [dayFiles]
  );
  const generalFiles = useMemo(
    () => dayFiles.filter((f) => normalizeDayFileType(f.fileType) === 'files'),
    [dayFiles]
  );
  const daySheetVenueName = useMemo(() => {
    const fromTour = String(tourDayVenueName || '').trim();
    if (fromTour) return fromTour;
    const fromEvents = events
      .map((e) => String(e.dayVenueSummary || e.venueName || '').trim())
      .find(Boolean);
    return fromEvents || '';
  }, [events, tourDayVenueName]);

  async function handleDownloadDaySheetPdf() {
    if (daySheetPdfBusy) return;
    try {
      setDaySheetPdfBusy(true);
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        const target = document.getElementById(daySheetExportNodeId);
        if (target) {
          const [{ default: html2canvas }, mod] = await Promise.all([import('html2canvas'), import('jspdf')]);
          if (mod?.jsPDF) {
            const canvas = await html2canvas(target, {
              backgroundColor: '#ffffff',
              useCORS: true,
              allowTaint: true,
              scale: 2,
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new mod.jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
            const pageW = 210;
            const pageH = 297;
            const margin = 6;
            const maxW = pageW - margin * 2;
            const maxH = pageH - margin * 2;
            const imgW = canvas.width || 1;
            const imgH = canvas.height || 1;
            const ratio = Math.min(maxW / imgW, maxH / imgH);
            const drawW = imgW * ratio;
            const drawH = imgH * ratio;
            const x = (pageW - drawW) / 2;
            const y = (pageH - drawH) / 2;
            pdf.addImage(imgData, 'PNG', x, y, drawW, drawH, undefined, 'FAST');
            pdf.save(`day-sheet_${tourId || 'tour'}_${dateKey || 'date'}.pdf`);
            return;
          }
        }
      }
      const mod = await import('jspdf');
      if (!mod?.jsPDF) return;
      await downloadDaySheetPdf({
        tourName: tourName || 'Untitled tour',
        dateKey,
        headline: daySheetHeadline,
        venueName: daySheetVenueName,
        bannerUrl: daySheetBannerUrl,
        backgroundUrl: daySheetBackgroundUrl,
        scheduleRows: daySheetRows,
        aftershowText: daySheetNotes,
        fileName: `day-sheet_${tourId || 'tour'}_${dateKey || 'date'}.pdf`,
      });
    } catch (err) {
      console.error('download day sheet pdf', err);
    } finally {
      setDaySheetPdfBusy(false);
    }
  }

  const getTabDraft = useCallback(
    (tabKey) => {
      if (Object.prototype.hasOwnProperty.call(drafts, tabKey)) return drafts[tabKey];
      return String(tabEntries[tabKey] || '');
    },
    [drafts, tabEntries]
  );

  const persistTabValue = useCallback(
    async (tabKey, rawValue) => {
      if (!uid || !tourId || !dateKey || !tabKey) return;
      let value = String(rawValue || '');
      if (tabKey === 'SCHEDULE') {
        value = serializeScheduleDoc(parseScheduleText(value)).trim();
      } else if (STRUCTURED_DAY_LIST_TABS.has(tabKey)) {
        value = serializeSetlistDoc(parseSetlistText(value)).trim();
      }
      const db = getFirestore();
      const entryId = `${tourId}__${tabKey}__${dateKey}`;
      const ref = doc(db, 'users', uid, 'calendarTabEntries', entryId);
      try {
        setSavingTabs((prev) => ({ ...prev, [tabKey]: true }));
        if (!value) {
          try {
            await deleteDoc(ref);
          } catch (e) {
            if (!String(e?.code || '').includes('not-found')) throw e;
          }
        } else {
          await setDoc(
            ref,
            {
              ...buildTabEntryPublishFields({
                tourId,
                tabKey,
                date: dateKey,
                draftText: value,
              }),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      } catch (err) {
        console.error('calendar day details save tab', tabKey, err);
      } finally {
        setSavingTabs((prev) => ({ ...prev, [tabKey]: false }));
      }
    },
    [uid, tourId, dateKey]
  );

  const onChangeTabDraft = useCallback(
    (tabKey, nextValue) => {
      setDrafts((prev) => ({ ...prev, [tabKey]: nextValue }));
      const timers = saveTimersRef.current;
      if (timers[tabKey]) clearTimeout(timers[tabKey]);
      timers[tabKey] = setTimeout(() => {
        persistTabValue(tabKey, nextValue);
      }, 700);
    },
    [persistTabValue]
  );

  // Keep the day-sheet aftershow draft in sync with what we load while not editing.
  useEffect(() => {
    if (!editMode) setDaySheetNotesDraft(String(daySheetNotes || ''));
  }, [daySheetNotes, editMode]);

  const persistDaySheetNotes = useCallback(
    async (nextValue) => {
      if (!uid || !tourId || !dateKey) return;
      const db = getFirestore();
      const ref = doc(db, 'users', uid, 'calendarDaySheets', `${tourId}__${dateKey}`);
      try {
        await setDoc(
          ref,
          { aftershowText: String(nextValue || ''), updatedAt: serverTimestamp() },
          { merge: true }
        );
        setDaySheetNotes(String(nextValue || ''));
      } catch (err) {
        console.error('calendar day details save day sheet notes', err);
      }
    },
    [uid, tourId, dateKey]
  );

  const onChangeDaySheetNotes = useCallback(
    (nextValue) => {
      setDaySheetNotesDraft(nextValue);
      const timers = saveTimersRef.current;
      if (timers.__daysheet) clearTimeout(timers.__daysheet);
      timers.__daysheet = setTimeout(() => persistDaySheetNotes(nextValue), 700);
    },
    [persistDaySheetNotes]
  );

  const uploadDayFile = useCallback(
    async (fileType) => {
      if (!uid || !tourId || !dateKey || fileBusy) return;
      try {
        setFileBusy(true);
        const result = await DocumentPicker.getDocumentAsync({
          type: '*/*',
          copyToCacheDirectory: true,
          multiple: false,
        });
        if (result.canceled || !result.assets?.[0]) return;
        const asset = result.assets[0];
        const fileName = String(asset.name || 'File').trim() || 'File';
        const uri = asset.uri;
        if (!uri) return;
        const response = await fetch(uri);
        const blob = await response.blob();
        const safeName = String(asset.name || `file-${Date.now()}`).replace(/[^\w.\-]+/g, '_');
        const path = `calendarDayFiles/${uid}/${tourId}/${dateKey}/${Date.now()}_${safeName}`;
        const storage = getStorage();
        const ref = storageRef(storage, path);
        await uploadBytes(ref, blob);
        const url = await getDownloadURL(ref);
        const db = getFirestore();
        await addDoc(collection(db, 'users', uid, 'calendarDayFiles'), {
          tourId,
          date: dateKey,
          name: fileName,
          url,
          fileType,
          storagePath: path,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setFilesNonce((n) => n + 1);
      } catch (err) {
        console.error('calendar day details upload file', err);
      } finally {
        setFileBusy(false);
      }
    },
    [uid, tourId, dateKey, fileBusy]
  );

  const removeDayFile = useCallback(
    async (id) => {
      if (!uid || !id) return;
      try {
        await deleteDoc(doc(getFirestore(), 'users', uid, 'calendarDayFiles', id));
        setFilesNonce((n) => n + 1);
      } catch (err) {
        console.error('calendar day details remove file', err);
      }
    },
    [uid]
  );

  const beginEdit = useCallback(() => {
    setDrafts({
      NOTES: String(tabEntries.NOTES || ''),
      RIDER_DAILY: String(tabEntries.RIDER_DAILY || tabEntries.RIDER || ''),
      RIDER_FULL: String(tabEntries.RIDER_FULL || ''),
      SCHEDULE: String(tabEntries.SCHEDULE || ''),
      SETLIST: String(tabEntries.SETLIST || ''),
      'GUEST LIST': String(tabEntries['GUEST LIST'] || ''),
      CONTACTS: String(tabEntries.CONTACTS || ''),
      MERCH: String(tabEntries.MERCH || ''),
      'TOUR PARTY': String(tabEntries['TOUR PARTY'] || ''),
      STAGE: String(tabEntries.STAGE || ''),
      LODGING: String(tabEntries.LODGING || ''),
    });
    setDaySheetNotesDraft(String(daySheetNotes || ''));
    setEditMode(true);
  }, [tabEntries, daySheetNotes]);

  const endEdit = useCallback(() => {
    const timers = saveTimersRef.current;
    Object.keys(timers).forEach((k) => {
      if (!timers[k]) return;
      clearTimeout(timers[k]);
      timers[k] = null;
      // Flush any pending debounced write so nothing is lost on close.
      if (k === '__daysheet') {
        persistDaySheetNotes(daySheetNotesDraft);
      } else {
        persistTabValue(k, getTabDraft(k));
      }
    });
    setEditMode(false);
  }, [persistDaySheetNotes, daySheetNotesDraft, persistTabValue, getTabDraft]);

  const renderEditFileList = (files, emptyLabel) => {
    if (!files.length) return <Text style={styles.editEmpty}>{emptyLabel}</Text>;
    return (
      <View style={styles.editFileList}>
        {files.map((f) => (
          <View key={`edit-file-${f.id}`} style={styles.editFileRow}>
            <TouchableOpacity
              style={styles.editFileLink}
              onPress={() => Linking.openURL(f.url).catch(() => {})}
            >
              <Ionicons
                name={isImageFileUrl(f.url) ? 'image-outline' : 'document-outline'}
                size={14}
                color={theme.brand.accent}
              />
              <Text style={styles.fileLinkText} numberOfLines={1}>
                {f.name}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.editFileRemove}
              onPress={() => removeDayFile(f.id)}
              accessibilityLabel={`Remove ${f.name}`}
            >
              <Ionicons name="trash-outline" size={15} color={theme.status?.error || '#e05a5a'} />
            </TouchableOpacity>
          </View>
        ))}
      </View>
    );
  };

  const renderAddFileBtn = (fileType, label) => (
    <TouchableOpacity
      style={[styles.editAddFileBtn, fileBusy && styles.editAddFileBtnDisabled]}
      onPress={() => uploadDayFile(fileType)}
      disabled={fileBusy}
    >
      <Ionicons name="attach-outline" size={15} color={theme.brand.accent} />
      <Text style={styles.editAddFileText}>{fileBusy ? 'Uploading…' : label}</Text>
    </TouchableOpacity>
  );

  const savingBadge = (tabKey) =>
    tabKey && savingTabs[tabKey] ? <Text style={styles.savingText}>Saving…</Text> : null;

  const editSectionProps = (id, title, tabKey) => ({
    styles,
    theme,
    id,
    title,
    description: SECTION_DESCRIPTIONS[id],
    expanded: isSectionExpanded(id),
    onToggle: toggleSection,
    rightAction: savingBadge(tabKey),
  });

  const STRUCTURED_EDIT_SECTIONS = [
    { id: 'setlist', tabKey: 'SETLIST' },
    { id: 'guestList', tabKey: 'GUEST LIST' },
    { id: 'contacts', tabKey: 'CONTACTS' },
    { id: 'merch', tabKey: 'MERCH' },
  ];

  function renderEditMode() {
    const venuesText = events
      .map((e) => String(e.dayVenueSummary || '').trim())
      .filter(Boolean)
      .join('\n');
    return (
      <View style={styles.editWrap}>
        {venuesText ? (
          <ModuleSection {...editSectionProps('venues', 'VENUES')}>
            <Text style={styles.editReadonlyNote}>Venues come from the tour planner and are read-only here.</Text>
            <View style={styles.detailBodyWrap}>{renderRichBody(venuesText, styles)}</View>
          </ModuleSection>
        ) : null}

        <ModuleSection {...editSectionProps('notes', 'NOTES', 'NOTES')}>
          <DayNotesEditor
            value={getTabDraft('NOTES')}
            onChange={(t) => onChangeTabDraft('NOTES', t)}
            theme={theme}
            placeholder="Add notes for this day..."
          />
        </ModuleSection>

        <ModuleSection {...editSectionProps('schedule', 'SCHEDULE', 'SCHEDULE')}>
          <ScheduleEditor
            mode="full"
            theme={theme}
            fullUnscroll
            value={getTabDraft('SCHEDULE')}
            onChange={(t) => onChangeTabDraft('SCHEDULE', t)}
          />
        </ModuleSection>

        <ModuleSection {...editSectionProps('rider', 'RIDER', 'RIDER_DAILY')}>
          <Text style={styles.editFieldLabel}>Daily</Text>
          <CalendarWriterEditor
            instanceKey={`edit-rider-daily-${dateKey}`}
            value={getTabDraft('RIDER_DAILY')}
            onChange={(t) => onChangeTabDraft('RIDER_DAILY', t)}
            placeholder="Daily rider…"
            theme={theme}
          />
          <Text style={[styles.editFieldLabel, { marginTop: 10 }]}>Full</Text>
          <CalendarWriterEditor
            instanceKey={`edit-rider-full-${dateKey}`}
            value={getTabDraft('RIDER_FULL')}
            onChange={(t) => onChangeTabDraft('RIDER_FULL', t)}
            placeholder="Full rider…"
            theme={theme}
          />
          <View style={{ marginTop: 8 }}>
            {renderEditFileList(riderFullFiles, 'No rider files yet.')}
            {renderAddFileBtn('riderFull', 'Add rider file')}
          </View>
        </ModuleSection>

        {STRUCTURED_EDIT_SECTIONS.map(({ id, tabKey }) => (
          <ModuleSection key={`edit-${id}`} {...editSectionProps(id, tabKey, tabKey)}>
            <StructuredDayListEditor
              tabKey={tabKey}
              mode="full"
              theme={theme}
              fullUnscroll
              value={getTabDraft(tabKey)}
              onChange={(t) => onChangeTabDraft(tabKey, t)}
            />
          </ModuleSection>
        ))}

        <ModuleSection {...editSectionProps('stage', 'STAGE', 'STAGE')}>
          <TextInput
            style={styles.editPlainInput}
            value={getTabDraft('STAGE')}
            onChangeText={(t) => onChangeTabDraft('STAGE', t)}
            multiline
            textAlignVertical="top"
            placeholder="Add stage notes…"
            placeholderTextColor={theme.text.secondary}
          />
          <View style={{ marginTop: 8 }}>
            {renderEditFileList(stageFiles, 'No stage files yet.')}
            {renderAddFileBtn('stage', 'Add stage file')}
          </View>
        </ModuleSection>

        <ModuleSection {...editSectionProps('files', 'FILES')}>
          {renderEditFileList(generalFiles, 'No files yet.')}
          {renderAddFileBtn('files', 'Add file')}
        </ModuleSection>

        <ModuleSection {...editSectionProps('tourParty', 'TOUR PARTY', 'TOUR PARTY')}>
          <TextInput
            style={styles.editPlainInput}
            value={getTabDraft('TOUR PARTY')}
            onChangeText={(t) => onChangeTabDraft('TOUR PARTY', t)}
            multiline
            textAlignVertical="top"
            placeholder="Add tour party…"
            placeholderTextColor={theme.text.secondary}
          />
        </ModuleSection>

        <ModuleSection {...editSectionProps('lodging', 'LODGING', 'LODGING')}>
          <TextInput
            style={styles.editPlainInput}
            value={getTabDraft('LODGING')}
            onChangeText={(t) => onChangeTabDraft('LODGING', t)}
            multiline
            textAlignVertical="top"
            placeholder="Add lodging…"
            placeholderTextColor={theme.text.secondary}
          />
        </ModuleSection>

        <ModuleSection {...editSectionProps('daySheet', 'DAY SHEET')}>
          <Text style={styles.editReadonlyNote}>
            The day sheet schedule is built from the SCHEDULE tab above. Add an aftershow / extra note below.
          </Text>
          <TextInput
            style={styles.editPlainInput}
            value={daySheetNotesDraft}
            onChangeText={onChangeDaySheetNotes}
            multiline
            textAlignVertical="top"
            placeholder="Aftershow / extra notes…"
            placeholderTextColor={theme.text.secondary}
          />
        </ModuleSection>
      </View>
    );
  }

  const sections = useMemo(() => {
    const venues = events
      .map((e) => String(e.dayVenueSummary || '').trim())
      .filter(Boolean)
      .join('\n');
    const schedulePreview = schedulePreviewText;
    const daySheetPreview = [
      schedulePreview ? `SCHEDULE\n${schedulePreview}` : '',
      daySheetNotes ? `AFTERSHOW\n${daySheetNotes}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
    const hasDaySheetPreview = Boolean(daySheetPreview || daySheetBannerUrl || daySheetBackgroundUrl);
    return [
      { id: 'venues', title: 'VENUES', body: venues },
      ...(hasDaySheetPreview
        ? [{ id: 'daySheetPreview', title: 'DAY SHEET PREVIEW', body: daySheetPreview }]
        : []),
      ...CALENDAR_TOP_TABS.map((tabLabel) => {
        if (tabLabel === 'DAY SHEET') return null;
        const tabKey = DAY_INFO_TAB_KEYS[tabLabel] || tabLabel;
        const bodyFromTab = String(tabEntries[tabKey] || '').trim();
        if (tabLabel === 'STAGE') {
          const body = [bodyFromTab, stagePlotUrl, stageInputListUrl].filter((x) => String(x || '').trim()).join('\n');
          return { id: 'stage', title: 'STAGE', body };
        }
        if (tabLabel === 'SCHEDULE') {
          const body = formatScheduleAsPlainText(String(tabEntries[tabKey] || ''));
          return { id: 'schedule', title: 'SCHEDULE', body };
        }
        if (tabLabel === 'SETLIST') {
          const body = formatSetlistAsPlainText(String(tabEntries[tabKey] || ''), STRUCTURED_DAY_LIST_LABELS.SETLIST);
          return { id: 'setlist', title: 'SETLIST', body, setlistRaw: bodyFromTab };
        }
        if (tabLabel === 'GUEST LIST') {
          const body = formatSetlistAsPlainText(String(tabEntries[tabKey] || ''), STRUCTURED_DAY_LIST_LABELS['GUEST LIST']);
          return { id: 'guestList', title: 'GUEST LIST', body };
        }
        if (tabLabel === 'CONTACTS') {
          const body = formatSetlistAsPlainText(String(tabEntries[tabKey] || ''), STRUCTURED_DAY_LIST_LABELS.CONTACTS);
          return { id: 'contacts', title: 'CONTACTS', body };
        }
        if (tabLabel === 'MERCH') {
          const body = formatSetlistAsPlainText(String(tabEntries[tabKey] || ''), STRUCTURED_DAY_LIST_LABELS.MERCH);
          return { id: 'merch', title: 'MERCH', body };
        }
        if (tabLabel === 'LODGING') {
          const plannerBody = formatLodgingDaySummary(tourFolderData, dateKey);
          const combined = [plannerBody, bodyFromTab].filter(Boolean).join('\n\n');
          return { id: 'lodging', title: 'LODGING', body: combined };
        }
        if (tabLabel === 'RIDER') {
          const dailyHtml = String(tabEntries.RIDER_DAILY || tabEntries.RIDER || '').trim();
          const fullHtml = String(tabEntries.RIDER_FULL || '').trim();
          return { id: 'rider', title: 'RIDER', dailyHtml, fullHtml, body: '' };
        }
        const sectionIdMap = {
          'TOUR PARTY': 'tourParty',
          RIDER: 'rider',
          SETLIST: 'setlist',
          'GUEST LIST': 'guestList',
          SCHEDULE: 'schedule',
          FILES: 'files',
          CONTACTS: 'contacts',
          MERCH: 'merch',
          NOTES: 'notes',
          LODGING: 'lodging',
        };
        const id = sectionIdMap[tabLabel] || tabLabel.toLowerCase().replace(/\s+/g, '');
        return { id, title: tabLabel, body: bodyFromTab };
      }),
    ].filter((sec) => {
      if (!sec) return false;
      if (sec.id === 'rider') {
        return riderSectionHasContent(sec.dailyHtml, sec.fullHtml, riderFullFiles.length);
      }
      if (sec.id === 'stage') return Boolean(String(sec.body || '').trim() || stageFiles.length);
      if (sec.id === 'files') return generalFiles.length > 0;
      if (sec.id === 'schedule') return !hasDaySheetPreview && Boolean(String(sec.body || '').trim());
      if (sec.id === 'daySheetPreview') {
        return Boolean(String(sec.body || '').trim() || daySheetBannerUrl || daySheetBackgroundUrl);
      }
      return Boolean(String(sec.body || '').trim());
    });
  }, [
    events,
    tabEntries,
    stagePlotUrl,
    stageInputListUrl,
    daySheetNotes,
    riderFullFiles.length,
    stageFiles.length,
    generalFiles.length,
    daySheetBannerUrl,
    daySheetBackgroundUrl,
    schedulePreviewText,
    daySheetRows.length,
    tourFolderData,
    dateKey,
  ]);
  const availableShareOptions = useMemo(() => {
    const availableTitles = new Set(
      sections.map((s) => {
        if (s.title === 'DAY SHEET PREVIEW') return 'DAY SHEET';
        return s.title;
      })
    );
    return DAY_SHARE_OPTIONS.filter((opt) => availableTitles.has(opt));
  }, [sections]);
  const displaySections = useMemo(() => {
    const filtered =
      !shareModulesAllowList || shareModulesAllowList.size === 0
        ? sections
        : sections.filter((sec) => shareModulesAllowList.has(shareModuleTokenForSection(sec)));

    if (!shareModulesOrderList?.length) return filtered;

    const rank = (sec) => {
      const token = shareModuleTokenForSection(sec);
      const idx = shareModulesOrderList.indexOf(token);
      return idx === -1 ? 9999 : idx;
    };
    return [...filtered].sort((a, b) => rank(a) - rank(b));
  }, [sections, shareModulesAllowList, shareModulesOrderList]);
  const openShareModal = useCallback(() => {
    setShareSelections((prev) => {
      if (prev.length) return prev;
      if (shareModulesAllowList?.size) {
        const fromUrl = availableShareOptions.filter((opt) => shareModulesAllowList.has(opt));
        if (fromUrl.length) return fromUrl;
      }
      return availableShareOptions;
    });
    setShareModalOpen(true);
  }, [availableShareOptions, shareModulesAllowList]);

  if (isSetlistOnlyShare && !detailsLoading) {
    return (
      <View style={styles.setlistShareRoot}>
        <View style={styles.setlistShareToolbar}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.title}>{tourName || 'Untitled tour'}</Text>
            <Text style={styles.sub}>Setlist · {setlistDateLabel}</Text>
          </View>
          {Platform.OS === 'web' ? (
            <TouchableOpacity style={styles.setlistSharePrintBtn} onPress={handlePrintSetlist}>
              <Ionicons name="print-outline" size={18} color={theme.brand.primary} />
              <Text style={styles.setlistSharePrintBtnText}>Print / PDF</Text>
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity style={styles.shareIconBtn} onPress={openShareModal} accessibilityLabel="Share setlist">
            <Ionicons name="paper-plane-outline" size={20} color={theme.brand.accent} />
          </TouchableOpacity>
        </View>
        <ScrollView style={styles.setlistShareScroll} contentContainerStyle={styles.setlistShareScrollContent}>
          <SetlistPaper
            variant="fullPage"
            dateLabel={setlistDateLabel}
            setlistRaw={setlistRaw}
            tourName={tourName}
            theme={theme}
          />
        </ScrollView>
        <Modal visible={shareModalOpen} transparent animationType="fade" onRequestClose={() => setShareModalOpen(false)}>
          <View style={styles.modalOverlay}>
            <Pressable style={styles.modalBackdrop} onPress={() => setShareModalOpen(false)} />
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>Share setlist</Text>
              <Text style={styles.sub}>Include SETLIST when sharing this day.</Text>
              <TextInput style={styles.input} value={generatedShareLink} editable={false} selectTextOnFocus />
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.closeBtn} onPress={() => setShareModalOpen(false)}>
                  <Text style={styles.closeBtnText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>{tourName || 'Untitled tour'}</Text>
            <Text style={styles.sub}>
              {dateKey || 'Unknown date'}
              {weekdayLabel ? ` · ${weekdayLabel}` : ''}
            </Text>
          </View>
          {isOwner ? (
            <TouchableOpacity
              style={[styles.editToggleBtn, editMode && styles.editToggleBtnActive]}
              onPress={() => (editMode ? endEdit() : beginEdit())}
              accessibilityLabel={editMode ? 'Finish editing' : 'Edit day'}
            >
              <Ionicons
                name={editMode ? 'checkmark' : 'create-outline'}
                size={16}
                color={editMode ? theme.button.primaryText || '#fff' : theme.brand.accent}
              />
              <Text style={[styles.editToggleText, editMode && styles.editToggleTextActive]}>
                {editMode ? 'Done' : 'Edit'}
              </Text>
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity style={styles.shareIconBtn} onPress={openShareModal} accessibilityLabel="Share day details">
            <Ionicons name="paper-plane-outline" size={20} color={theme.brand.accent} />
          </TouchableOpacity>
        </View>
      </View>
      <ScrollView style={styles.content} contentContainerStyle={styles.contentScroll} keyboardShouldPersistTaps="handled">
        <View style={styles.card}>
          <View style={styles.cardHeadRow}>
            <Text style={styles.sectionTitle}>{editMode ? 'Edit day' : 'Details'}</Text>
            {editMode ? (
              <Text style={styles.editHint}>Changes save automatically</Text>
            ) : null}
          </View>
          {detailsLoading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={theme.brand.accent} />
              <Text style={styles.sub}>Loading day details…</Text>
            </View>
          ) : null}
          {editMode ? renderEditMode() : null}
          {!editMode && displaySections.map((sec) => {
            return (
              <ModuleSection
                key={`day-detail-${sec.id}`}
                styles={styles}
                theme={theme}
                id={sec.id}
                title={sec.title}
                description={SECTION_DESCRIPTIONS[sec.id]}
                expanded={isSectionExpanded(sec.id)}
                onToggle={toggleSection}
              >
                {(
                  sec.id === 'files' ? (
                    <View style={styles.detailBodyWrap}>
                      {generalFiles.map((f) => (
                        <DayFileAttachment key={`files-${f.id}`} file={f} styles={styles} theme={theme} />
                      ))}
                    </View>
                  ) : sec.id === 'rider' ? (
                    <View style={styles.detailBodyWrap}>
                      {!isWriterHtmlEmpty(sec.dailyHtml) ? (
                        <View style={styles.writerSectionBlock}>
                          <Text style={styles.writerSectionLabel}>Daily</Text>
                          <WriterHtmlView html={sec.dailyHtml} theme={theme} />
                        </View>
                      ) : null}
                      {!isWriterHtmlEmpty(sec.fullHtml) ? (
                        <View style={styles.writerSectionBlock}>
                          <Text style={styles.writerSectionLabel}>Full</Text>
                          <WriterHtmlView html={sec.fullHtml} theme={theme} />
                        </View>
                      ) : null}
                      {riderFullFiles.length ? (
                        <View style={styles.detailFileAttachmentsList}>
                          {riderFullFiles.map((f) => (
                            <DayFileAttachment key={`rider-upload-${f.id}`} file={f} styles={styles} theme={theme} />
                          ))}
                        </View>
                      ) : null}
                    </View>
                  ) : sec.id === 'stage' ? (
                    <View style={styles.detailBodyWrap}>
                      {String(sec.body || '').trim() ? <View>{renderRichBody(sec.body, styles)}</View> : null}
                      {stageFiles.length ? (
                        <View style={styles.detailFileAttachmentsList}>
                          {stageFiles.map((f) => (
                            <DayFileAttachment key={`stage-file-${f.id}`} file={f} styles={styles} theme={theme} />
                          ))}
                        </View>
                      ) : null}
                    </View>
                  ) : sec.id === 'daySheetPreview' ? (
                    <View style={styles.detailBodyWrap}>
                      <TouchableOpacity
                        style={[styles.downloadPdfBtn, daySheetPdfBusy && styles.downloadPdfBtnDisabled]}
                        onPress={handleDownloadDaySheetPdf}
                        disabled={daySheetPdfBusy}
                      >
                        <Ionicons name="download-outline" size={14} color={theme.button.primaryText || '#fff'} />
                        <Text style={styles.downloadPdfBtnText}>{daySheetPdfBusy ? 'Preparing PDF...' : 'Download day sheet PDF'}</Text>
                      </TouchableOpacity>
                      {daySheetBannerUrl ? (
                        <TouchableOpacity style={styles.fileLinkRow} onPress={() => Linking.openURL(daySheetBannerUrl).catch(() => {})}>
                          <Ionicons name="image-outline" size={14} color={theme.brand.accent} />
                          <Text style={styles.fileLinkText} numberOfLines={1}>
                            Open banner asset
                          </Text>
                        </TouchableOpacity>
                      ) : null}
                      {daySheetBackgroundUrl ? (
                        <TouchableOpacity
                          style={styles.fileLinkRow}
                          onPress={() => Linking.openURL(daySheetBackgroundUrl).catch(() => {})}
                        >
                          <Ionicons name="images-outline" size={14} color={theme.brand.accent} />
                          <Text style={styles.fileLinkText} numberOfLines={1}>
                            Open background asset
                          </Text>
                        </TouchableOpacity>
                      ) : null}
                      <View style={styles.daySheetPaperWrap}>
                        <DaySheetPaper
                          theme={theme}
                          headline={daySheetHeadline}
                          venueName={daySheetVenueName}
                          bannerUrl={daySheetBannerUrl}
                          backgroundUrl={daySheetBackgroundUrl}
                          scheduleRows={daySheetRows}
                          aftershowText={daySheetNotes}
                          exportNodeId={daySheetExportNodeId}
                          compact
                        />
                      </View>
                    </View>
                  ) : sec.id === 'setlist' ? (
                    <View style={styles.detailBodyWrap}>
                      {Platform.OS === 'web' ? (
                        <TouchableOpacity style={styles.downloadPdfBtn} onPress={handlePrintSetlist}>
                          <Ionicons name="print-outline" size={14} color={theme.button.primaryText || '#fff'} />
                          <Text style={styles.downloadPdfBtnText}>Print / save setlist PDF</Text>
                        </TouchableOpacity>
                      ) : null}
                      <View style={styles.setlistPaperWrap}>
                        <SetlistPaper
                          variant="inline"
                          dateLabel={setlistDateLabel}
                          setlistRaw={sec.setlistRaw || setlistRaw}
                          tourName={tourName}
                          theme={theme}
                        />
                      </View>
                    </View>
                  ) : (
                    <View style={styles.detailBodyWrap}>{renderNotesBody(sec.body, styles, theme)}</View>
                  )
                )}
              </ModuleSection>
            );
          })}
        </View>
      </ScrollView>
      <Modal visible={shareModalOpen} transparent animationType="fade" onRequestClose={() => setShareModalOpen(false)}>
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalBackdrop} onPress={() => setShareModalOpen(false)} />
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Share day info</Text>
            <Text style={styles.sub}>Select sections to include for {dateKey || 'this day'}.</Text>
            <View style={{ marginTop: 8, marginBottom: 8 }}>
              {availableShareOptions.map((opt) => {
                const checked = shareSelections.includes(opt);
                return (
                  <TouchableOpacity
                    key={`share-day-${opt}`}
                    style={styles.optionRow}
                    onPress={() => setShareSelections((prev) => (checked ? prev.filter((x) => x !== opt) : [...prev, opt]))}
                  >
                    <Ionicons name={checked ? 'checkbox' : 'square-outline'} size={18} color={theme.brand.accent} />
                    <Text style={styles.optionText}>{opt}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TextInput style={styles.input} value={generatedShareLink} editable={false} selectTextOnFocus />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.closeBtn} onPress={() => setShareModalOpen(false)}>
                <Text style={styles.closeBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function createStyles(theme) {
  const borderColor = theme?.border?.primary || 'rgba(255,255,255,0.16)';
  const cardBg = theme?.background?.card || theme?.background?.secondary || 'rgba(255,255,255,0.04)';
  const elevatedBg = theme?.background?.elevated || theme?.background?.secondary || 'rgba(255,255,255,0.08)';
  const inputBg = theme?.input?.background || theme?.background?.secondary || 'rgba(255,255,255,0.06)';
  const inputText = theme?.input?.text || theme?.text?.primary || '#ffffff';
  return StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      paddingHorizontal: 14,
      paddingBottom: 14,
      paddingTop: Platform.OS === 'web' ? 112 : 96,
      alignItems: 'center',
    },
    header: { marginTop: 0, marginBottom: 10, width: '100%', maxWidth: 760, alignSelf: 'center' },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    shareIconBtn: {
      width: 38,
      height: 38,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor,
      backgroundColor: elevatedBg,
    },
    title: { color: theme.text.primary, fontSize: 22, fontWeight: '800' },
    sub: { color: theme.text.secondary, fontSize: 13, marginTop: 2 },
    content: { flex: 1, minHeight: 0, width: '100%', maxWidth: 760, alignSelf: 'center' },
    contentScroll: { gap: 12, paddingBottom: 28, width: '100%' },
    card: {
      borderRadius: 12,
      backgroundColor: cardBg,
      borderWidth: 1,
      marginBottom: 12,
      borderColor,
      width: '100%',
      padding: 12,
    },
    sectionTitle: { color: theme.text.primary, fontSize: 15, fontWeight: '800', marginBottom: 10 },
    loadingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 10,
    },
    optionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
    optionText: { color: theme.text.primary, fontSize: 13, fontWeight: '700' },
    input: {
      marginTop: 8,
      borderWidth: 1,
      borderColor,
      borderRadius: 10,
      backgroundColor: inputBg,
      color: inputText,
      minHeight: 42,
      paddingHorizontal: 10,
    },
    detailSection: {
      borderWidth: 1,
      borderColor,
      borderRadius: 10,
      marginBottom: 8,
      overflow: 'hidden',
    },
    detailHead: {
      paddingHorizontal: 10,
      paddingVertical: 8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 6,
      backgroundColor: elevatedBg,
    },
    detailTitle: { color: theme.text.primary, fontSize: 13, fontWeight: '800' },
    detailBodyWrap: { paddingHorizontal: 10, paddingVertical: 10 },
    writerSectionBlock: { marginBottom: 14, gap: 6 },
    writerSectionLabel: {
      color: theme.text.secondary,
      fontSize: 11,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    detailBody: { color: theme.text.primary, fontSize: 13, lineHeight: 18 },
    detailBodyBold: { fontWeight: '800' },
    detailBodyHeading: { color: theme.text.primary, fontSize: 14, lineHeight: 20, fontWeight: '800' },
    detailDivider: {
      height: 1,
      backgroundColor: borderColor,
      marginVertical: 6,
      marginHorizontal: 2,
    },
    fileLinkRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
    },
    fileLinkText: {
      color: theme.text.link || theme.brand.accent,
      fontSize: 13,
      fontWeight: '700',
      flex: 1,
    },
    detailFileBlock: {
      marginBottom: 14,
    },
    detailFileAttachmentsList: {
      marginTop: 10,
      width: '100%',
    },
    detailFileImageTouch: {
      width: '100%',
      marginTop: 8,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: cardBg,
      borderWidth: 1,
      borderColor,
    },
    detailFileImagePreview: {
      width: '100%',
      height: 280,
    },
    downloadPdfBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      marginBottom: 10,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderWidth: 1,
      borderColor: theme.button.primaryBackground || theme.brand.accent,
      backgroundColor: theme.button.primaryBackground || theme.brand.accent,
    },
    downloadPdfBtnDisabled: {
      opacity: 0.65,
    },
    downloadPdfBtnText: {
      color: theme.button.primaryText || '#ffffff',
      fontSize: 13,
      fontWeight: '800',
    },
    daySheetPaperWrap: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 4,
    },
    setlistPaperWrap: {
      width: '100%',
      marginTop: 8,
    },
    setlistShareRoot: {
      flex: 1,
      width: '100%',
      paddingHorizontal: 14,
      paddingBottom: 14,
      paddingTop: Platform.OS === 'web' ? 112 : 96,
      alignItems: 'center',
    },
    setlistShareToolbar: {
      width: '100%',
      maxWidth: 816,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginBottom: 16,
    },
    setlistShareScroll: { flex: 1, width: '100%' },
    setlistShareScrollContent: { alignItems: 'center', paddingBottom: 32 },
    setlistSharePrintBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 10,
      borderWidth: 1,
      borderColor,
      backgroundColor: elevatedBg,
    },
    setlistSharePrintBtnText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme?.brand?.primary || theme?.text?.primary || '#fff',
    },
    modalOverlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    modalCard: {
      width: '100%',
      maxWidth: 440,
      backgroundColor: elevatedBg,
      borderRadius: 14,
      padding: 16,
      borderWidth: 1,
      borderColor,
      zIndex: 1,
    },
    modalTitle: { color: theme.text.primary, fontSize: 17, fontWeight: '800', marginBottom: 8 },
    modalActions: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 10 },
    closeBtn: { paddingHorizontal: 12, paddingVertical: 8 },
    closeBtnText: { color: theme.text.primary, fontWeight: '700' },
    editToggleBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      height: 38,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor,
      backgroundColor: elevatedBg,
    },
    editToggleBtnActive: {
      backgroundColor: theme.button.primaryBackground || theme.brand.accent,
      borderColor: theme.button.primaryBackground || theme.brand.accent,
    },
    editToggleText: { color: theme.brand.accent, fontSize: 13, fontWeight: '800' },
    editToggleTextActive: { color: theme.button.primaryText || '#ffffff' },
    cardHeadRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 10,
    },
    editHint: { color: theme.text.secondary, fontSize: 11, fontWeight: '600' },
    moduleBlock: { width: '100%', marginBottom: 4 },
    moduleHead: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: borderColor,
    },
    moduleHeadCollapsed: { borderBottomColor: 'transparent' },
    moduleHeadText: { flex: 1, minWidth: 140 },
    moduleHeadRight: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: 8,
    },
    moduleActions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
    moduleCollapseBtn: {
      width: 30,
      height: 30,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor,
      backgroundColor: cardBg,
    },
    moduleTitle: {
      color: theme.text.primary,
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    moduleDesc: { color: theme.text.secondary, fontSize: 12, marginTop: 3, lineHeight: 16 },
    moduleBody: { width: '100%', paddingTop: 12, paddingBottom: 4 },
    editWrap: { gap: 4 },
    savingText: { color: theme.text.secondary, fontSize: 11, fontStyle: 'italic' },
    editReadonlyNote: { color: theme.text.secondary, fontSize: 11, marginBottom: 8 },
    editFieldLabel: {
      color: theme.text.secondary,
      fontSize: 11,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 4,
    },
    editEmpty: { color: theme.text.secondary, fontSize: 12, marginBottom: 8 },
    editFileList: { gap: 6, marginBottom: 8 },
    editFileRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor,
      borderRadius: 8,
      paddingHorizontal: 8,
      paddingVertical: 6,
      backgroundColor: cardBg,
    },
    editFileLink: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
    editFileRemove: { padding: 4 },
    editAddFileBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 8,
      borderWidth: 1,
      borderColor,
      backgroundColor: cardBg,
    },
    editAddFileBtnDisabled: { opacity: 0.6 },
    editAddFileText: { color: theme.brand.accent, fontSize: 12, fontWeight: '700' },
    editPlainInput: {
      borderWidth: 1,
      borderColor,
      borderRadius: 8,
      backgroundColor: cardBg,
      color: inputText,
      minHeight: 70,
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 13,
    },
  });
}
